/**
 * Reading a position off a photograph of the chartplotter.
 *
 * Three decisions here are product decisions, not implementation details.
 *
 * **1. The image never leaves the phone.** OCR runs on-device, in a WebAssembly
 * worker. A photograph of a chartplotter is not a photograph of two numbers: it
 * shows the vessel's track, its waypoints, its route, and every AIS target
 * around it. For a product whose entire proposition is discretion, uploading
 * that to a server -- let alone storing it -- would be indefensible. Nothing in
 * this module touches the network, the image is held in memory only, and it is
 * discarded the moment the read finishes.
 *
 * **2. OCR proposes; it never decides.** {@link readPosition} returns
 * *candidates*, and the caller is required to plot them on the chart for the
 * captain to confirm. A misread digit is not a cosmetic bug -- it is an
 * application filed for the wrong bay. Same principle as the KMZ merge tool and
 * the conflict hint: the machine narrows it down, a human commits.
 *
 * **3. It degrades to absent.** The engine is ~2 MB of wasm plus a ~2 MB
 * language model, lazily loaded on first use. If those assets are not deployed
 * (see tools/fetch_ocr_assets.py) or the device cannot run them, the camera
 * option simply does not appear. The other two ways of entering a position are
 * not worse for it.
 *
 * Preprocessing is where most of the accuracy comes from, and the reason is
 * visible in any real plotter photo: the text is **light on a dark panel**, and
 * Tesseract is trained on dark-on-light. Inverting when the crop is
 * predominantly dark is worth more than any parameter tuning.
 */

import { scanForPositions, type PositionCandidate } from '@abg/ui'

/** Where the wasm core and the language model are served from, on our origin. */
const ASSET_BASE = '/ocr/'

export type OcrAvailability = 'ready' | 'no-assets' | 'unsupported'

export interface OcrResult {
  candidates: PositionCandidate[]
  /** The raw text, shown so a captain can see why it failed when it fails. */
  text: string
  /** Milliseconds the recognition took, for the "this is slow" hint. */
  elapsedMs: number
  /**
   * Which attempt produced the reading that was kept, or 'none'. Diagnostic:
   * if real photographs turn out to land on one attempt every time, the rest
   * are waste and can go -- and `tools/ocr_tuning` is how that is decided.
   */
  polarity: string
}

let availability: OcrAvailability | null = null

/**
 * Can this device do it, and are the assets deployed?
 *
 * Asked rather than assumed, because the failure mode of getting this wrong is
 * a button that appears, spins for thirty seconds and fails -- and the honest
 * answer is to hide it. Two questions, and both have been wrong here:
 *
 * **Can WebAssembly actually compile?** `typeof WebAssembly === 'undefined'`
 * is a *feature* check, and the object exists in every browser this app runs
 * in. What can fail is **compilation**: a Content-Security-Policy without
 * `'wasm-unsafe-eval'` in `script-src` makes every `WebAssembly.Module()`
 * throw. That was true of this deployment, so the feature could never have
 * worked in production, and nothing in the codebase could have noticed --
 * `deploy/nginx.conf` is not something a unit test reads. Compiling an
 * eight-byte module is the smallest question with the right answer.
 *
 * **Is the core the worker will ask for actually there?** This used to check
 * `eng.traineddata` alone, which was present while the core wrappers were not,
 * so it reported `ready` and the read 404'd. Both are checked now. The SIMD
 * wrapper stands for the pair: `fetch_ocr_assets.py` fetches both `-lstm`
 * variants together, so one being served means both are.
 */
export async function ocrAvailability(): Promise<OcrAvailability> {
  if (availability) return availability
  if (typeof WebAssembly === 'undefined' || typeof Worker === 'undefined') {
    availability = 'unsupported'
    return availability
  }
  try {
    // Magic number and version, and nothing else: a valid empty module.
    new WebAssembly.Module(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0]))
  } catch {
    // Almost always the CSP rather than the engine. Either way this device
    // cannot run it, and 'unsupported' is what hides the button.
    availability = 'unsupported'
    return availability
  }
  try {
    const [model, core] = await Promise.all([
      fetch(`${ASSET_BASE}eng.traineddata`, { method: 'HEAD' }),
      fetch(`${ASSET_BASE}tesseract-core-simd-lstm.wasm.js`, { method: 'HEAD' }),
    ])
    availability = model.ok && core.ok ? 'ready' : 'no-assets'
  } catch {
    availability = 'no-assets'
  }
  return availability
}

// The worker is expensive to start (~2-4 s on a phone) and cheap to keep, so it
// is a singleton. Idle-terminated because it holds tens of megabytes and a
// captain photographs a plotter once, not continuously.
type TesseractWorker = {
  recognize: (image: unknown) => Promise<{ data: { text: string } }>
  setParameters: (params: Record<string, string>) => Promise<unknown>
  terminate: () => Promise<unknown>
}

let workerPromise: Promise<TesseractWorker> | null = null
let idleTimer: number | undefined

const IDLE_TERMINATE_MS = 60_000

async function getWorker(onProgress?: (fraction: number) => void): Promise<TesseractWorker> {
  if (workerPromise) return workerPromise
  workerPromise = (async () => {
    // Dynamic import so none of this is in the main bundle. A captain who never
    // uses the camera never downloads the engine.
    const { createWorker, PSM } = await import('tesseract.js')
    const worker = (await createWorker('eng', 1, {
      workerPath: `${ASSET_BASE}worker.min.js`,
      corePath: ASSET_BASE,
      langPath: ASSET_BASE,
      // Without this, tesseract.js wraps the worker script in a Blob and
      // starts the worker from a `blob:` URL -- which `worker-src 'self'`
      // refuses, so the engine never starts. Measured on the deployment: a
      // direct `new Worker('/ocr/worker.min.js')` is allowed and the blob is
      // not, and the console says so plainly.
      //
      // The blob wrapper exists to let `workerPath` be cross-origin. Ours is
      // our own origin by design (see tools/fetch_ocr_assets.py), so it buys
      // nothing here and its only effect would be to make us add `blob:` to
      // `worker-src`. This is the cheaper side of that trade.
      workerBlobURL: false,
      // tesseract.js asks for `eng.traineddata.gz` unless told otherwise, and
      // fetch_ocr_assets.py stores the plain file -- so the default produced a
      // 404 and the engine hung at "loading language traineddata 0%".
      //
      // Fixed here rather than by fetching a .gz, so the model's provenance
      // stays one pinned file from tessdata_fast instead of a compressed copy
      // from a third CDN. It costs about 2.5 MB more on first use, once per
      // device per engine version -- the OCR_CACHE in sw.js survives deploys,
      // which is what makes that a one-off rather than a recurring bill.
      gzip: false,
      logger: (message: { status: string; progress: number }) => {
        if (message.status === 'recognizing text') onProgress?.(message.progress)
      },
    })) as unknown as TesseractWorker

    await worker.setParameters({
      // A cropped coordinate panel is one uniform block of text, not a page.
      tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
      // **No character whitelist, deliberately.** It used to be
      // `0123456789NSEWnsew` and so on, on the theory that constraining the
      // alphabet helps. Measured against a noisy read it does the opposite,
      // and dangerously: the engine spends speckle on the characters it is
      // allowed, so the output filled with `N`, `S`, `E` and `e` -- and
      // `scanForPositions` requires a hemisphere letter before it will believe
      // a number is a coordinate. The whitelist was therefore *manufacturing*
      // the one token that turns noise into a candidate, which is how a
      // photograph of 69°37.282'N came back as `1 n 1 e`.
      //
      // Without it, noise decodes as ordinary letters the scanner discards.
      // The trade is real and the right way round: a misread hemisphere now
      // costs a miss, where before it bought a confident wrong answer.
      tessedit_char_whitelist: '',
    })
    return worker
  })()
  return workerPromise
}

function touchIdleTimer(): void {
  if (idleTimer) window.clearTimeout(idleTimer)
  idleTimer = window.setTimeout(() => void terminateOcr(), IDLE_TERMINATE_MS)
}

export async function terminateOcr(): Promise<void> {
  const pending = workerPromise
  workerPromise = null
  if (idleTimer) window.clearTimeout(idleTimer)
  idleTimer = undefined
  if (!pending) return
  try {
    ;(await pending).terminate()
  } catch {
    // Already gone. Nothing to do and nothing worth telling anyone.
  }
}

/**
 * Recognise a position in an image region.
 *
 * `source` is whatever the browser will draw: a `<video>` frame, a `<canvas>`,
 * or an `ImageBitmap` from a file the captain picked. `region` is the guide box
 * in source pixels -- cropping to it is what keeps SOG, COG and depth out of
 * the text in the first place.
 */
export async function readPosition(
  source: CanvasImageSource,
  region: { x: number; y: number; width: number; height: number },
  onProgress?: (fraction: number) => void,
): Promise<OcrResult> {
  const started = performance.now()
  const worker = await getWorker(onProgress)
  const attempted: string[] = []

  /**
   * The guide box first, then the whole frame.
   *
   * The crop is first because it does real work: `position.fixtures.json`
   * records `34.70NM` -- a distance to waypoint -- being read as latitude
   * 34 N, and narrowing the image is what keeps SOG, COG and depth out of the
   * text in the first place.
   *
   * The whole frame is here because **the crop is what was failing.** On
   * ABG's photograph 9 the frame scores 1.00 and the crop 0.07, at every
   * scale from 0.3x to 2x and every page segmentation mode -- and the glyphs
   * are the same 21 px in both, so it is not scale. Tesseract wants the
   * surrounding structure. Framing that loses a line is common enough that a
   * fallback beats asking a captain to re-aim on a wet deck.
   */
  const regions = [region, wholeFrame(source)]

  // Stop at the first attempt whose text yields a position. Which one wins is
  // strongly screen-dependent and there is no gentle degradation -- a pipeline
  // either reads a panel or returns nothing usable -- so ordering by how many
  // of ABG's photographs each covered is the whole strategy.
  for (const [index, area] of regions.entries()) {
    const scope = index === 0 ? 'guide' : 'frame'
    if (area.width < 1 || area.height < 1) continue
    for (const attempt of ATTEMPTS) {
      const { data } = await worker.recognize(render(source, area, attempt))
      const candidates = scanForPositions(data.text)
      attempted.push(`${scope}/${attempt.label}:\n${data.text.trim()}`)
      if (candidates.length > 0) {
        touchIdleTimer()
        return {
          candidates,
          text: data.text,
          elapsedMs: Math.round(performance.now() - started),
          polarity: `${scope}/${attempt.label}`,
        }
      }
    }
  }

  touchIdleTimer()
  return {
    candidates: [],
    // Every attempt's reading, labelled. This is the surface somebody tunes
    // against, and the reason `tools/ocr_tuning` exists is that guessing from
    // one reading does not converge.
    text: attempted.join('\n\n'),
    elapsedMs: Math.round(performance.now() - started),
    polarity: 'none',
  }
}

/**
 * The whole of whatever was handed in, in its own pixels.
 *
 * A video frame reports its size differently from a bitmap or an image, and
 * getting it wrong here would silently crop to a corner -- so each case is
 * named rather than duck-typed on `width`.
 */
function wholeFrame(source: CanvasImageSource): {
  x: number
  y: number
  width: number
  height: number
} {
  if (source instanceof HTMLVideoElement) {
    return { x: 0, y: 0, width: source.videoWidth, height: source.videoHeight }
  }
  if (source instanceof HTMLImageElement) {
    return { x: 0, y: 0, width: source.naturalWidth, height: source.naturalHeight }
  }
  if (source instanceof HTMLCanvasElement || source instanceof ImageBitmap) {
    return { x: 0, y: 0, width: source.width, height: source.height }
  }
  // SVGImageElement and VideoFrame: not sources this app hands in, and a
  // guess here would be a crop to nowhere.
  return { x: 0, y: 0, width: 0, height: 0 }
}

interface Attempt {
  label: string
  /** Multiplier on the crop. Below 1 in every case that works; see below. */
  scale: number
  /** Separable Gaussian radius in pixels, 0 for none. */
  blur: number
  invert: boolean
}

/**
 * The pipelines that read ABG's plotter photographs, in the order to try them.
 *
 * **Measured, not reasoned.** Two rounds of reasoning about this produced a
 * read of `1 n 1 e` and then a wall of speckle; `tools/ocr_tuning` ran ninety
 * variants against eleven real photographs and these four cover all of them.
 * Item 35 has the numbers. Three things in that result are worth knowing
 * before touching this list:
 *
 * - **Downscaling is most of it.** The code here used to upscale 2x, on the
 *   theory that Tesseract wants a large cap height. These photographs are
 *   576x1024 to 2048x2048 and the glyphs in the big ones are ~180 px tall
 *   already, so upscaling made an enormous image out of a too-large one. It
 *   also removes the LCD's pixel grid for free, which is what the old adaptive
 *   threshold was turning into speckle.
 * - **No thresholding at all.** Tesseract's own binarisation beat every
 *   variant that pre-thresholded, including the percentile-clipped adaptive
 *   one this file used to carry.
 * - **The polarity result is the opposite of what this file used to claim.**
 *   The two day-mode screens (dark text on light) are read after *inverting*
 *   them, and the two night-mode ones (light on dark) without. So light-on-dark
 *   is what wins here, and "Tesseract gives markedly worse results on the
 *   inverse" -- asserted in this file for months -- is not what the
 *   photographs say. The white padding applied after inversion may be part of
 *   why; that is untested, so the combinations are ported exactly as measured
 *   rather than as understood.
 *
 * Ordered by how many photographs each covered, cheapest first among equals.
 * A pass costs little now -- a 0.3x crop is about a fortieth of the pixels the
 * old 2x upscale produced -- so four attempts are faster than the one attempt
 * this replaces.
 */
const ATTEMPTS: readonly Attempt[] = [
  { label: 'small-inverted', scale: 0.3, blur: 0, invert: true },
  { label: 'small', scale: 0.3, blur: 0, invert: false },
  { label: 'small-blurred', scale: 0.3, blur: 2, invert: false },
  { label: 'full-inverted', scale: 1, blur: 0, invert: true },
]

/** Crop, scale, blur, invert and pad, per one {@link Attempt}. */
function render(
  source: CanvasImageSource,
  region: { x: number; y: number; width: number; height: number },
  attempt: Attempt,
): HTMLCanvasElement {
  let canvas = scaled(source, region, attempt.scale)
  if (attempt.blur > 0) canvas = blurred(canvas, attempt.blur)
  if (attempt.invert) canvas = inverted(canvas)
  return padded(canvas, 24)
}

/**
 * Crop and scale, halving repeatedly on the way down.
 *
 * The rig used OpenCV's `INTER_AREA`, which is a box filter. A single
 * `drawImage` to 0.3x is bilinear whatever `imageSmoothingQuality` says, and
 * bilinear downsampling by a large factor *aliases* -- which on a photograph
 * of an LCD turns the pixel grid into coarse moire instead of removing it,
 * and removing it is the point. Halving repeatedly is a box filter in effect,
 * so this stays close to what was measured.
 */
function scaled(
  source: CanvasImageSource,
  region: { x: number; y: number; width: number; height: number },
  scale: number,
): HTMLCanvasElement {
  let width = Math.max(1, Math.round(region.width))
  let height = Math.max(1, Math.round(region.height))
  const targetWidth = Math.max(1, Math.round(region.width * scale))
  const targetHeight = Math.max(1, Math.round(region.height * scale))

  let canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  let context = canvas.getContext('2d')
  if (!context) return canvas
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, region.x, region.y, region.width, region.height, 0, 0, width, height)

  while (width > targetWidth * 2 && height > targetHeight * 2) {
    const next = document.createElement('canvas')
    next.width = Math.max(targetWidth, Math.floor(width / 2))
    next.height = Math.max(targetHeight, Math.floor(height / 2))
    const nextContext = next.getContext('2d')
    if (!nextContext) break
    nextContext.imageSmoothingEnabled = true
    nextContext.imageSmoothingQuality = 'high'
    nextContext.drawImage(canvas, 0, 0, next.width, next.height)
    canvas = next
    width = next.width
    height = next.height
    context = nextContext
  }

  if (width === targetWidth && height === targetHeight) return canvas
  const final = document.createElement('canvas')
  final.width = targetWidth
  final.height = targetHeight
  const finalContext = final.getContext('2d')
  if (!finalContext) return canvas
  finalContext.imageSmoothingEnabled = true
  finalContext.imageSmoothingQuality = 'high'
  finalContext.drawImage(canvas, 0, 0, targetWidth, targetHeight)
  return final
}

/**
 * Separable Gaussian, in JavaScript rather than with `ctx.filter`.
 *
 * `CanvasRenderingContext2D.filter` would be one line and is not available on
 * every iOS version this app has to run on -- and a blur that silently does
 * nothing on some phones is the class of defect this codebase keeps writing
 * items about. Cheap here regardless: it runs on an already-downscaled crop.
 */
function blurred(source: HTMLCanvasElement, radius: number): HTMLCanvasElement {
  const { width, height } = source
  const context = source.getContext('2d', { willReadFrequently: true })
  if (!context) return source
  const image = context.getImageData(0, 0, width, height)
  const pixels = image.data

  // A radius-2 binomial kernel: the 5-tap [1 4 6 4 1] / 16, which is the
  // discrete Gaussian the rig's 5x5 approximates.
  const kernel = [1, 4, 6, 4, 1]
  const weight = 16
  const grey = new Float32Array(width * height)
  for (let p = 0; p < grey.length; p += 1) {
    const i = p * 4
    grey[p] =
      0.299 * (pixels[i] ?? 0) + 0.587 * (pixels[i + 1] ?? 0) + 0.114 * (pixels[i + 2] ?? 0)
  }

  const pass = (input: Float32Array, horizontal: boolean): Float32Array => {
    const output = new Float32Array(input.length)
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        let total = 0
        for (let k = -radius; k <= radius; k += 1) {
          const sx = horizontal ? Math.min(width - 1, Math.max(0, x + k)) : x
          const sy = horizontal ? y : Math.min(height - 1, Math.max(0, y + k))
          total += (input[sy * width + sx] ?? 0) * (kernel[k + radius] ?? 0)
        }
        output[y * width + x] = total / weight
      }
    }
    return output
  }

  const smoothed = pass(pass(grey, true), false)
  for (let p = 0; p < smoothed.length; p += 1) {
    const value = smoothed[p] ?? 0
    const i = p * 4
    pixels[i] = value
    pixels[i + 1] = value
    pixels[i + 2] = value
    pixels[i + 3] = 255
  }
  context.putImageData(image, 0, 0)
  return source
}

/** Every pixel flipped. */
function inverted(source: HTMLCanvasElement): HTMLCanvasElement {
  const context = source.getContext('2d', { willReadFrequently: true })
  if (!context) return source
  const image = context.getImageData(0, 0, source.width, source.height)
  const pixels = image.data
  for (let i = 0; i < pixels.length; i += 4) {
    pixels[i] = 255 - (pixels[i] ?? 0)
    pixels[i + 1] = 255 - (pixels[i + 1] ?? 0)
    pixels[i + 2] = 255 - (pixels[i + 2] ?? 0)
  }
  context.putImageData(image, 0, 0)
  return source
}

/**
 * A white border. Tesseract's layout analysis wants quiet space and does badly
 * without it, and every pipeline the rig ranked carried one -- so its
 * contribution is not isolated in that data, and it is kept because removing
 * an untested step from a measured recipe is how a measured recipe stops being
 * one.
 */
function padded(source: HTMLCanvasElement, border: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = source.width + border * 2
  canvas.height = source.height + border * 2
  const context = canvas.getContext('2d')
  if (!context) return source
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(source, border, border)
  return canvas
}

/** Turn a file the captain picked into something drawable. */
export async function bitmapFromFile(file: File): Promise<ImageBitmap> {
  return createImageBitmap(file)
}
